/** /org/soluciones/:slug — el doc padre de una solución: su ficha (qué hace y cómo trabaja), quiénes la integran, con qué clientes trabaja y (según scope) quién con cuál. */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Building2, CheckCircle2, Briefcase } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatCard } from '@/components/ui/stat-card';
import { InternalTable, EmptyState, LoaderBlock } from '@/components/internal/shared';
import { useOrgAccess, useOrgSolution } from '@/hooks/useOrg';
import { OrgSolutionSheet } from '@/components/org/OrgSolutionSheet';
import { OrgAssignmentsTable, OrgClientLink, OrgPersonLink, type OrgPerson } from '@/components/org/org-ui';

export function OrgSolutionPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const q = useOrgSolution(slug);
  const access = useOrgAccess();
  if (q.isLoading) return <LoaderBlock />;
  if (q.isError && q.error.data?.code !== 'NOT_FOUND') return <EmptyState title={t('org.home.error', 'No se pudo cargar el Org Service. Reintentá en unos segundos.')} />;
  if (q.isError || !q.data) return <EmptyState title={t('org.solution.not_found', 'Esa solución no existe en el Org Service.')} />;
  const { solution, people, clients, assignments, assignmentsScope, sheet } = q.data;
  const confirmed = assignments.filter((a) => a.status === 'confirmed').length;
  type ClientRow = (typeof clients)[number];
  const peopleCols = [
    { key: 'name', label: t('org.table.name', 'Nombre'), render: (p: OrgPerson) => <OrgPersonLink email={p.email} name={p.full_name} /> },
    { key: 'role', label: t('org.table.role', 'Puesto'), render: (p: OrgPerson) => <span className="text-muted-foreground">{p.role_title ?? '—'}</span> },
    { key: 'team', label: t('org.card.area', 'Área'), render: (p: OrgPerson) => <span className="text-muted-foreground">{[p.area, p.team].filter(Boolean).join(' · ') || '—'}</span> },
  ];
  const clientCols = [
    { key: 'client', label: t('org.table.client', 'Cliente'), render: (c: ClientRow) => <OrgClientLink clientId={c.client_id} name={c.display_name} /> },
    { key: 'pod', label: t('org.table.pod', 'Pod'), render: (c: ClientRow) => <span className="text-muted-foreground">{c.pod ?? '—'}</span> },
    { key: 'people', label: t('org.table.people', 'Personas'), align: 'right' as const, render: (c: ClientRow) => c.people },
    { key: 'confirmed', label: t('org.table.confirmed', 'Confirmadas'), align: 'right' as const, render: (c: ClientRow) => c.confirmed },
  ];
  return (
    <div className="space-y-4 pb-24">
      <Link to="/org?tab=solutions" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"><ArrowLeft className="h-4 w-4" />{t('org.solution.back', 'Soluciones')}</Link>
      <div>
        <h1 className="text-2xl font-bold">{solution.name}</h1>
        <p className="text-sm text-muted-foreground">
          {t('org.solution.meta', 'Fuente: {{source}} · Patrón de acceso {{pattern}} · Áreas: {{areas}}', {
            source: t(`org.source.${solution.source}`, solution.source),
            pattern: solution.accessPattern,
            areas: solution.communityAreas.join(', ') || '—',
          })}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t('org.table.people', 'Personas')} value={people.length} icon={Users} tone="blue" size="sm" />
        <StatCard label={t('org.table.clients', 'Clientes')} value={clients.length} icon={Building2} tone="purple" size="sm" />
        <StatCard label={t('org.solution.assignments', 'Asignaciones visibles')} value={assignments.length} icon={Briefcase} tone="default" size="sm" />
        <StatCard label={t('org.table.confirmed', 'Confirmadas')} value={confirmed} icon={CheckCircle2} tone="green" size="sm" />
      </div>
      <Tabs defaultValue="sheet">
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="sheet">{t('org.tabs.sheet', 'Ficha')}</TabsTrigger>
          <TabsTrigger value="people">{t('org.tabs.people', 'Personas')}</TabsTrigger>
          <TabsTrigger value="clients">{t('org.tabs.clients', 'Clientes')}</TabsTrigger>
          <TabsTrigger value="assignments">{t('org.solution.title_assignments', 'Quién con quién')}</TabsTrigger>
        </TabsList>
        <TabsContent value="sheet"><OrgSolutionSheet slug={solution.slug} sheet={sheet} isAdmin={Boolean(access.data?.canAdmin)} /></TabsContent>
        <TabsContent value="people">{people.length ? <InternalTable columns={peopleCols} rows={people} rowKey={(p) => String(p.employee_id)} /> : <EmptyState title={t('org.solution.no_people', 'Nadie del Community Map tiene esta solución como principal.')} />}</TabsContent>
        <TabsContent value="clients">{clients.length ? <InternalTable columns={clientCols} rows={clients} rowKey={(c) => c.client_id} /> : <EmptyState title={t('org.solution.no_clients', 'Sin clientes con gente asignada este mes.')} />}</TabsContent>
        <TabsContent value="assignments">
          <p className="mb-2 text-xs text-muted-foreground">{t(`org.solution.scope_${assignmentsScope}`, assignmentsScope)}</p>
          <OrgAssignmentsTable rows={assignments} hide={['solution']} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
